import { CheckBox } from "@mui/icons-material";
import { Box, Button, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useGetOrderDetailsQuery, useUpscalePrintMutation } from "api/apiSlice";
import ImageDialog from "components/ImageDialog";
import LazyImageWithSkeleton from "components/LazyImageWithSkeleton";
import SelectionMark from "components/SelectionMark";

import SmallDialog from "components/SmallDialog";
import { useSnackbar } from "notistack";

import React, { Fragment, useState, useEffect } from "react";

const Title = ({ data }) => {
  const theme = useTheme();
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 0.5, sm: 1, md: 1 }}
      useFlexGap
      sx={{
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography sx={{ fontWeight: 600 }}>{data.data.product}</Typography>
      <Typography
        sx={{
          fontWeight: 800,
          paddingBottom: "10px",
          color: theme.palette.yellows[700],
        }}
      >
        {data.data.lora_type}
      </Typography>
    </Stack>
  );
};

const OrderDetails = ({ isOpen, setIsOpen, setIsClose, order }) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { data, error, isLoading } = useGetOrderDetailsQuery({
    _id: order.uuid,
  });

  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const [selectedImages, setselectedImages] = useState([]);

  const [success, setsuccess] = useState(false);

  const [submit] = useUpscalePrintMutation();

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.toString()}</div>;

  const handleOpenImageDialog = (index) => {
    setCurrentImage(index);
    setImageDialogOpen(true);
  };

  const handleSelectImages = (index) => {
    setselectedImages((current) => {
      if (current.includes(index)) {
        // Deselect if already selected
        return current.filter((item) => item !== index);
      } else if (current.length < 2) {
        // Only add if fewer than 2 are selected
        return [...current, index];
      } else {
        // Do nothing if already 2 selected
        return current;
      }
    });
  };

  const handleSubmit = async () => {
    const urls = data.data.images.filter((img, index) =>
      selectedImages.includes(index)
    );

    const res = await submit({ orderId: order.uuid, urls: urls }).unwrap();
    enqueueSnackbar("Order placed!", {
      variant: "success",
    });
  };

  return (
    <Fragment>
      <SmallDialog
        title={<Title data={data} />}
        open={isOpen}
        setOpen={setIsOpen}
        setClose={setIsClose}
      >
        {success ? (
          <>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ marginBottom: "20px" }}
            >
              <Typography sx={{ alignSelf: "center" }}>
                Select 2 images{" "}
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.5, sm: 1, md: 1 }}
              useFlexGap
              sx={{
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {data.data.images.map((img, index) => (
                <>
                  <Box
                    onClick={() => handleSelectImages(index)}
                    sx={{
                      position: "relative",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "scale(1.03)",
                        cursor: "pointer",
                      },
                    }}
                  >
                    <LazyImageWithSkeleton
                      src={img}
                      alt={"order_image"}
                      width="200px"
                    />
                    {selectedImages.includes(index) && (
                      <Paper
                        elevation={3}
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          bgcolor: theme.palette.yellows[700], // Use theme blue, or 'blue' for plain CSS
                          color: "white",
                          px: 1.9,
                          py: 1.5,
                          borderRadius: "25px 0px 5px 0px",
                          fontWeight: "bold",
                          fontSize: "12px",
                          zIndex: 10,
                          userSelect: "none",
                        }}
                      >
                        {" "}
                        <Typography
                          sx={{ fontSize: "14px", fontWeight: "700" }}
                        >
                          {selectedImages.indexOf(index) + 1}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </>
              ))}
            </Stack>
            {selectedImages.length !== 0 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ marginTop: "20px" }}
              >
                <Button variant="contained" onClick={handleSubmit}>
                  SUBMIT
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Stack
            direction={{ xs: "column", sm: "column" }}
            spacing={{ xs: 0.5, sm: 1, md: 1 }}
            useFlexGap
            sx={{
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CheckBox
              sx={{ marginTop: "300px", fontSize: "100px", color: "green" }}
            />
            <Typography
              sx={{
                fontSize: "33px",
                fontWeight: "700",
                color: theme.palette.yellows[700],
              }}
            >
              Order submitted successfully
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Your order has been submitted successfully, our support team will
              email you soon.
            </Typography>
          </Stack>
        )}
      </SmallDialog>

      <ImageDialog
        images={data.data.images}
        open={imageDialogOpen}
        setOpen={setImageDialogOpen}
        index={currentImage}
        title={<Title data={data} />}
      />
    </Fragment>
  );
};

export default OrderDetails;
